import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Theme,
  Typography,
  useTheme
} from '@mui/material';
import { DialogCloseIcon, FailureIcon } from '../../../assets/SvgIcons';
import { LoadingButton } from '@mui/lab';
import { FileUpload } from '../../commonComponents';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { fileToBase64 } from '../../../utils/utilityFunctions';

// ==================== INTERFACES ====================
export interface DocumentConfig {
  uploadFormats: string[];
  maxFiles: number;
  maxFileSize: string;
}

export interface MFCPopupProps {
  open: boolean;
  handleClose: () => void;
  status?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  errorTitle?: string;
  errorSubtitle?: string;
  successTitle?: string;
  successSubtitle?: string;
  documentConfig?: DocumentConfig;
  allowMultipleFiles?: boolean;
  onUpload?: (files: string) => Promise<void>;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | false;
  dialogWidth?: string;
  showCloseButton?: boolean;
}

// ==================== DEFAULT VALUES ====================
const DEFAULT_CONFIG: DocumentConfig = {
  uploadFormats: ['image/jpeg', 'image/png'],
  maxFiles: 1,
  maxFileSize: '2MB'
};

const DEFAULT_PROPS: Partial<MFCPopupProps> = {
  title: 'Upload QR Code',
  subtitle: 'Please upload your MFC QR Code below',
  buttonText: 'Upload & Continue',
  errorTitle: 'Something Went Wrong while redirecting from MFC',
  errorSubtitle: 'Please try again',
  maxWidth: 'sm',
  dialogWidth: '80dvw',
  showCloseButton: false,
  successTitle: 'Success!',
  successSubtitle: 'File uploaded successfully'
};

// ==================== MAIN COMPONENT ====================
export default function MFCPopup({
  open,
  handleClose,
  status = "",
  title = DEFAULT_PROPS.title,
  subtitle = DEFAULT_PROPS.subtitle,
  buttonText = DEFAULT_PROPS.buttonText,
  errorTitle = DEFAULT_PROPS.errorTitle,
  errorSubtitle = DEFAULT_PROPS.errorSubtitle,
  documentConfig = DEFAULT_CONFIG,
  onUpload,
  onError,
  maxWidth = DEFAULT_PROPS.maxWidth,
  dialogWidth = DEFAULT_PROPS.dialogWidth,
  showCloseButton = DEFAULT_PROPS.showCloseButton
}: MFCPopupProps): JSX.Element {

  const theme = useTheme();
  const [qrFile, setQrFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (file: File | null) => {
    setQrFile(file);
    if (file) setUploadError('');
  };

  const handleUpload = async () => {
    if (!qrFile) {
      setUploadError('Please upload a file.');
      onError?.('No file selected');
      return;
    }
    setIsUploading(true);
    setUploadError('');
    try {
      const base64File = await fileToBase64(qrFile);
      const base64Only = base64File?.split(',')[1];
      console.log('base64', base64File);
      await onUpload?.(base64Only);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Upload failed';
      setUploadError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseDialog = () => {
    handleClose();
    setQrFile(null);
    setUploadError('');
    setIsUploading(false);
  };

  // // ==================== SUCCESS STATE ====================
  // if (status === 'success') {
  //   return (
  //     <Dialog
  //       open={open}
  //       maxWidth={maxWidth}
  //       sx={(theme: Theme) => ({
  //         '& .MuiPaper-root': {
  //           borderRadius: '25px',
  //           backgroundColor: theme.palette.common.white,
  //           padding: "10px",
  //           width: dialogWidth,
  //           maxHeight: "80%"
  //         },
  //       })}
  //       aria-labelledby="mfc-popup-success"
  //     >
  //       <DialogContent
  //         sx={{
  //           display: "flex",
  //           flexDirection: "column",
  //           justifyContent: "center",
  //           alignItems: "center",
  //           rowGap: "12px",
  //           padding: "30px 20px",
  //           overflow: "hidden"
  //         }}
  //       >
  //         <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
  //           <circle cx="12" cy="12" r="10" fill="currentColor" />
  //           <path d="M8 12L10.5 14.5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  //         </svg>

  //         <Typography variant="h2" sx={{ lineHeight: "25px", color: theme.palette.primary.main }}>
  //           {successTitle}
  //         </Typography>

  //         <Typography variant="body2" sx={{
  //           lineHeight: "22px",
  //           color: theme.palette.text.secondary,
  //           textAlign: "center"
  //         }}>
  //           {successSubtitle}
  //         </Typography>
  //       </DialogContent>

  //       <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
  //         <LoadingButton
  //           variant="contained"
  //           onClick={handleCloseDialog}
  //           sx={(theme: Theme) => ({
  //             ...theme.typography.body_600,
  //             padding: "12px 24px",
  //             borderRadius: '20px',
  //             minWidth: '140px'
  //           })}
  //         >
  //           Done
  //         </LoadingButton>
  //       </DialogActions>
  //     </Dialog>
  //   );
  // }

  // ==================== ERROR STATE ====================
  if (status === 'error') {
    return (
      <Dialog
        open={open}
        maxWidth={maxWidth}
        sx={(theme: Theme) => ({
          '& .MuiPaper-root': {
            borderRadius: '25px',
            backgroundColor: theme.palette.common.white,
            padding: "10px",
            width: dialogWidth,
            maxHeight: "80%"
          },
        })}
        aria-labelledby="mfc-popup-error"
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            rowGap: "12px",
            padding: "20px 10px",
            overflow: "hidden"
          }}
        >
          <FailureIcon width="35" height="35" />

          <Typography
            variant="body_500"
            sx={{
              letterSpacing: "0.01px",
              color: theme.palette.error[500]
            }}
          >
            {errorTitle}
          </Typography>
          <Typography
            variant="body_500"
            sx={{
              lineHeight: "25px",
              letterSpacing: "0.01px",
              color: theme.palette.error[500]
            }}
          >
            {errorSubtitle}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <LoadingButton
            variant="contained"
            sx={(theme: Theme) => ({
              ...theme.typography.body_600,
              padding: "12px 24px",
              borderRadius: '10px',
              minWidth: '140px'
            })}
            onClick={handleCloseDialog}
          >
            Close
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  }

  // ==================== UPLOAD UI ====================
  return (
    <Dialog
      open={open}
      disableEnforceFocus
      maxWidth={maxWidth}
      sx={(theme: Theme) => ({
        '& .MuiPaper-root': {
          borderRadius: '25px',
          backgroundColor: theme.palette.common.white,
          padding: "10px",
          width: dialogWidth,
          maxHeight: "80%"
        },
      })}
      aria-labelledby="mfc-popup-upload"
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", p: 2 }}>
        <Box>
          <Typography
            variant="h2"
            sx={{ lineHeight: "25px", color: theme.palette.text.primary }}
          >
            {title}
          </Typography>
        </Box>

        {showCloseButton && (
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{ p: 0.5 }}
          >
            <DialogCloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Content */}
      <DialogContent sx={{ maxHeight: "60vh", overflowY: "auto", p: 2 }}>
        <Box sx={{ display: "flex", flexDirection: 'column', alignItems: 'center', rowGap: 3 }}>
          {subtitle && (
            <Grid container spacing={2} alignItems="center" justifyContent="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="overline_400"
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                >
                  {subtitle}
                </Typography>
              </Grid>
            </Grid>
          )}

          <Grid container justifyContent="center">
            <Grid item xs={12} md={8}>
              <FileUpload
                acceptedFileTypes={documentConfig.uploadFormats}
                maxFileSize={documentConfig.maxFileSize}
                maxFiles={documentConfig.maxFiles}
                onFileChange={handleFileChange}
                error={uploadError}
                setError={setUploadError}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ justifyContent: "center", p: 3 }}>
        <LoadingButton
          variant="contained"
          loading={isUploading}
          disabled={!qrFile || isUploading}
          sx={(theme: Theme) => ({
            ...theme.typography.body_600,
            padding: "12px 32px",
            width: "60%",
            borderRadius: '20px',
            fontSize: '1rem'
          })}
          onClick={handleUpload}
        >
          {buttonText}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
